import { useState, useEffect, useCallback, useRef } from "react";
import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import type { paths } from "../../api/generated/api-types";

export type PublicationLimitsResponse =
  paths["/settings/publication-limits"]["get"]["responses"]["200"]["content"]["application/json"];

export type UpdatePublicationLimitsRequest =
  paths["/settings/publication-limits"]["put"]["requestBody"]["content"]["application/json"];

// Единая форма тела ошибки бэкенда, как в useDashboardActions.
interface ErrorBody {
  // Отсутствует, когда бэкенд ответил ошибкой без разобранного тела: сообщение
  // тогда собирается из кода ответа.
  error?: string;
}

export interface PublicationLimitsData {
  articleMaxPosts: number;
  sentenceMaxPosts: number;
  updatedAt: string;
  updatedByName: string | null;
}

export type PublicationLimitsState =
  | { kind: "loading" }
  | ({ kind: "data" } & PublicationLimitsData)
  | ({ kind: "saving" } & PublicationLimitsData)
  // lastData — последние успешно загруженные лимиты; null, если ошибка
  // произошла на самой первой загрузке (CONF-07 ещё ни разу не отвечал 200).
  | { kind: "error"; message: string; lastData: PublicationLimitsData | null };

export interface UsePublicationLimitsResult {
  state: PublicationLimitsState;
  save: (values: UpdatePublicationLimitsRequest) => void;
  retry: () => void;
}

function toData(response: PublicationLimitsResponse): PublicationLimitsData {
  return {
    articleMaxPosts: response.articleMaxPosts,
    sentenceMaxPosts: response.sentenceMaxPosts,
    updatedAt: response.updatedAt,
    updatedByName: response.updatedByName,
  };
}

export function usePublicationLimits(): UsePublicationLimitsResult {
  const [state, setState] = useState<PublicationLimitsState>({ kind: "loading" });
  const lastDataRef = useRef<PublicationLimitsData | null>(null);

  const load = useCallback(() => {
    setState({ kind: "loading" });
    fetchWithRefresh("/api/settings/publication-limits")
      .then(async (response) => {
        if (response.ok) {
          const data = toData((await response.json()) as PublicationLimitsResponse);
          lastDataRef.current = data;
          setState({ kind: "data", ...data });
        } else {
          const errorBody = (await response.json()) as ErrorBody;
          setState({
            kind: "error",
            message: errorBody.error ?? "Не удалось загрузить лимиты заспамленности",
            lastData: lastDataRef.current,
          });
        }
      })
      .catch(() => {
        setState({ kind: "error", message: "Ошибка сети", lastData: lastDataRef.current });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback((values: UpdatePublicationLimitsRequest) => {
    const previousData = lastDataRef.current;
    // The form that calls save() only renders once limits have loaded at
    // least once, so previousData is always set in real use; save() is a
    // no-op otherwise rather than sending a PUT with no prior committed state.
    if (previousData !== null) {
      setState({
        kind: "saving",
        ...previousData,
        articleMaxPosts: values.articleMaxPosts,
        sentenceMaxPosts: values.sentenceMaxPosts,
      });

      fetchWithRefresh("/api/settings/publication-limits", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      })
        .then(async (response) => {
          if (response.ok) {
            const data = toData((await response.json()) as PublicationLimitsResponse);
            lastDataRef.current = data;
            setState({ kind: "data", ...data });
          } else {
            const errorBody = (await response.json()) as ErrorBody;
            setState({
              kind: "error",
              message: errorBody.error ?? "Не удалось сохранить лимиты заспамленности",
              lastData: previousData,
            });
          }
        })
        .catch(() => {
          setState({ kind: "error", message: "Ошибка сети", lastData: previousData });
        });
    }
  }, []);

  return { state, save, retry: load };
}
